/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Render schema for eligibility response
 */

import _ from 'lodash';
import { Button, Tooltip } from 'antd';
import { Help } from '@icon-park/react';

import { changeDateFormat } from 'utils/dateParser';
import { EMPTY_VALUE, EMPTY_VALUE_FOR_DATE_RANGE, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { EMPTY_VALUES_LIST } from 'app-constants/eligibility';
import { customLabelMapping } from 'app-constants/eligibility/fhirLabelMapping';

const listStyle = 'list-square m-0 ml-3 ps-10';

const renderDate = (date?: string): string => {
	return date ? changeDateFormat(date, YEAR_MONTH_DATE_FORMAT) || EMPTY_VALUE : EMPTY_VALUE;
};

const renderDateRange = (startDate?: string, endDate?: string): string => {
	const start = startDate ? changeDateFormat(startDate, YEAR_MONTH_DATE_FORMAT) : '';
	const end = endDate ? changeDateFormat(endDate, YEAR_MONTH_DATE_FORMAT) : '';

	return `${start || EMPTY_VALUE_FOR_DATE_RANGE} - ${end || EMPTY_VALUE_FOR_DATE_RANGE}`;
};

const renderContacts = (contacts?: Record<string, string>): React.ReactNode | null => {
	if (_.isEmpty(contacts)) {
		return null;
	}

	return (
		<ul className={listStyle}>
			{_.map(contacts, (value, key) => (
				<li key={key} className='mb-0'>{`${_.startCase(key)} - ${value}`}</li>
			))}
		</ul>
	);
};

const renderArrayItems = (items: string[]): React.ReactNode => {
	return (
		<ul className={listStyle}>
			{_.map(items, (value, key) => (
				<li key={key} className='mb-0'>
					{value}
				</li>
			))}
		</ul>
	);
};
const renderObjItems = (items: Record<string, string>): React.ReactNode => {
	return (
		<ul className={listStyle}>
			{_.map(items, (value, key) => (
				<li key={key} className='mb-0'>{`${_.startCase(key)} - ${value}`}</li>
			))}
		</ul>
	);
};

const prepareItem = (label: string, index: number, value: string | React.ReactNode) => {
	return {
		label: _.get(customLabelMapping, label, label),
		dataKey: _.toString(index),
		render: () => value,
	};
};

// function to render nested objects
const renderNestedObjects = (obj: Record<string, Record<string, string | Record<string, string>>>): JSX.Element => {
	const objKeys = _.keys(obj);
	const isSingleContact = objKeys.length === 1;

	// format value
	const formatValue = (value: AnyType): React.ReactNode => {
		if (_.isArray(value)) {
			return value.join(', ');
		}
		if (_.isObject(value)) {
			return _.map(value, (v, k) => `${_.startCase(k)}: ${v}`).join(', ');
		}
		return value;
	};

	// render item for the list
	const renderListItem = (reactKey: string, label: string, value: AnyType) => (
		<li key={reactKey} style={{ marginBottom: 0 }}>
			{_.isEmpty(label) ? (
				formatValue(value)
			) : (
				<>
					<span style={{ color: '#595959' }}>{_.startCase(label)}</span> - {formatValue(value)}
				</>
			)}
		</li>
	);

	// render details of the contact point
	const renderDetails = (details: AnyType): JSX.Element => {
		if (_.isArray(details)) {
			return <ul className={listStyle}>{_.map(details, (item, index) => renderListItem(`item-${index}`, '', item))}</ul>;
		}

		if (_.isObject(details)) {
			return (
				<ul className={listStyle}>
					{_.flatMap(details, (value, key) => {
						if (_.isArray(value)) {
							return (value as AnyType[]).map((v: AnyType, i: number) => renderListItem(`${key}-${i}`, `${key} ${i + 1}`, v));
						}
						if (_.isObject(value)) {
							return _.map(value, (v, k) => renderListItem(`${key}-${k}`, k, v));
						}
						return renderListItem(key, key, value);
					})}
				</ul>
			);
		}
		return <span style={{ marginLeft: 8 }}>{_.isBoolean(details) ? _.toString(details) : details}</span>;
	};

	return (
		<div>
			{_.map(obj, (details, key) => (
				<div key={key} style={{ marginBottom: isSingleContact ? 0 : 8, marginLeft: 8 }}>
					{!isSingleContact && <strong style={{ fontSize: 13 }}>{key}</strong>}

					{renderDetails(details)}
				</div>
			))}
		</div>
	);
};

const renderItems = (items: EligibilityResponse.ItemType[]): IRendererSchema[] => {
	return _.compact(
		_.map(items, ({ label, value }, index) => {
			if (_.isString(value) && !_.includes(EMPTY_VALUES_LIST, value)) {
				return prepareItem(label, index, value);
			} else if (_.isArray(value) && !_.isEmpty(value)) {
				const itemValue = renderArrayItems(value);
				return prepareItem(label, index, itemValue);
			} else if (_.isObject(value) && !_.isEmpty(value)) {
				const hasNestedObjects = _.some(value, (v) => _.isObject(v));
				if (hasNestedObjects) {
					const nestedValue = renderNestedObjects(value as Record<string, Record<string, string | Record<string, string>>>);
					return prepareItem(label, index, nestedValue);
				}
				const objValue = renderObjItems(value as Record<string, string>);
				return prepareItem(label, index, objValue);
			}
			return null;
		}),
	);
};

const getRequestLocationRenderSchema = (locationName?: string): IRendererSchema => {
	return {
		dataKey: '',
		label: (
			<div>
				{'Service Location'}
				<Tooltip title='This is the location as specified in the input at the time of requesting.'>
					<Button type='text' shape='circle' icon={<Help />} />
				</Tooltip>
			</div>
		),
		render: () => locationName || EMPTY_VALUE,
	};
};

export { renderContacts, renderDate, renderDateRange, renderItems, getRequestLocationRenderSchema };
